import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'

export const FuelLogForm = ({ onSave, onClose }) => {
  const today = format(new Date(), 'yyyy-MM-dd')
  const [logDate,        setLogDate]        = useState(today)
  const [gallons,        setGallons]        = useState('')
  const [pricePerGallon, setPricePerGallon] = useState('')
  const [totalCost,      setTotalCost]      = useState('')
  const [mileage,        setMileage]        = useState('')
  const [station,        setStation]        = useState('')
  const [isFullTank,     setIsFullTank]     = useState(true)
  const [saving,         setSaving]         = useState(false)
  const [error,          setError]          = useState('')

  // Auto-calculate total cost
  useEffect(() => {
    const g = parseFloat(gallons)
    const p = parseFloat(pricePerGallon)
    if (!isNaN(g) && !isNaN(p)) {
      setTotalCost((g * p).toFixed(2))
    }
  }, [gallons, pricePerGallon])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const g = parseFloat(gallons)
    const p = parseFloat(pricePerGallon)
    const t = parseFloat(totalCost)
    const m = parseInt(mileage)
    if (!g || g <= 0) { setError('Enter gallons.'); return }
    if (!p || p <= 0) { setError('Enter price per gallon.'); return }
    if (!m || m <= 0) { setError('Enter mileage.'); return }
    setSaving(true)
    setError('')
    try {
      await onSave({
        log_date:        logDate,
        gallons:         g,
        price_per_gallon: p,
        total_cost:      isNaN(t) ? g * p : t,
        mileage:         m,
        station:         station.trim() || null,
        is_full_tank:    isFullTank,
      })
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
    }
  }

  const inputCls = 'w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
  const lbl = (t) => <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">{t}</label>

  return (
    <Portal>
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-text-primary">Log Fuel</h2>
          <button type="button" onClick={onClose} className="text-text-muted p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>{lbl('Date')}
              <input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} className={inputCls} />
            </div>
            <div>{lbl('Mileage *')}
              <input type="number" value={mileage} onChange={(e) => setMileage(e.target.value)}
                placeholder="45000" className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>{lbl('Gallons *')}
              <input type="number" step="0.001" value={gallons} onChange={(e) => setGallons(e.target.value)}
                placeholder="12.5" className={inputCls} />
            </div>
            <div>{lbl('$/Gallon *')}
              <input type="number" step="0.001" value={pricePerGallon} onChange={(e) => setPricePerGallon(e.target.value)}
                placeholder="3.45" className={inputCls} />
            </div>
            <div>{lbl('Total ($)')}
              <input type="number" step="0.01" value={totalCost} onChange={(e) => setTotalCost(e.target.value)}
                placeholder="auto" className={inputCls} />
            </div>
          </div>

          <div>{lbl('Station')}
            <input type="text" value={station} onChange={(e) => setStation(e.target.value)}
              placeholder="Shell, Costco…" className={inputCls} />
          </div>

          {/* Full tank toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <button type="button" onClick={() => setIsFullTank((v) => !v)}
              className={cn(
                'relative w-10 h-6 rounded-full transition-colors flex-shrink-0',
                isFullTank ? 'bg-primary' : 'bg-border',
              )}>
              <span className={cn(
                'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all',
                isFullTank ? 'left-4.5' : 'left-0.5',
              )} />
            </button>
            <span className="text-sm font-medium text-text-primary">Full tank</span>
          </label>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary">Cancel</button>
          <button type="submit" disabled={saving}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <><Spinner size="sm" /> Saving…</> : 'Log Fuel'}
          </button>
        </div>
      </form>
    </div>
    </Portal>
  )
}
